import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { TeamLeadService } from '../../services/teamLeadService';

const AttendanceManagement = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTelecaller, setSelectedTelecaller] = useState('');
  const [attendanceStatus, setAttendanceStatus] = useState({});

  const { data: teamMembersData, isLoading } = useQuery({
    queryKey: ['teamLeadMembers', user?.id],
    queryFn: () => TeamLeadService.getTeamMembers(user?.id),
    enabled: !!user?.id,
  });

  const teamMembers = teamMembersData?.data || [];

  const handleMarkAttendance = async (telecallerId, status) => {
    if (!confirm(`Mark ${teamMembers.find(m => m.id === telecallerId)?.name || 'this member'} as ${status}?`)) {
      return;
    }

    try {
      // In a real implementation, this would call an attendance service
      setAttendanceStatus(prev => ({ ...prev, [telecallerId]: status }));
      alert('Attendance marked successfully!');
    } catch (error) {
      alert('Error marking attendance: ' + error.message);
    }
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'present') return 'bg-green-100 text-green-700';
    if (s === 'halfday') return 'bg-yellow-100 text-yellow-700';
    if (s === 'absent') return 'bg-red-100 text-red-700';
    if (s === 'office_leave') return 'bg-blue-100 text-blue-700';
    return 'bg-gray-100 text-gray-700';
  };

  const statusCounts = {
    present: Object.values(attendanceStatus).filter(s => s === 'present').length,
    halfday: Object.values(attendanceStatus).filter(s => s === 'halfday').length,
    absent: Object.values(attendanceStatus).filter(s => s === 'absent').length,
    office_leave: Object.values(attendanceStatus).filter(s => s === 'office_leave').length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/teamlead/more')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Attendance Management</h1>
          <p className="text-gray-600 mt-1">Mark and review daily attendance</p>
        </div>
      </div>

      {/* Date Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4">
          <Calendar size={20} className="text-gray-400" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Attendance Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Present</p>
              <p className="text-2xl font-bold text-gray-900">{statusCounts.present}</p>
            </div>
            <CheckCircle size={24} className="text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Half Day</p>
              <p className="text-2xl font-bold text-gray-900">{statusCounts.halfday}</p>
            </div>
            <Clock size={24} className="text-yellow-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Absent</p>
              <p className="text-2xl font-bold text-gray-900">{statusCounts.absent}</p>
            </div>
            <XCircle size={24} className="text-red-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Office Leave</p>
              <p className="text-2xl font-bold text-gray-900">{statusCounts.office_leave}</p>
            </div>
            <AlertCircle size={24} className="text-blue-600" />
          </div>
        </div>
      </div>

      {/* Team Members Attendance */}
      {isLoading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading team members...</p>
        </div>
      ) : teamMembers.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <Users size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No team members</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Mark Attendance</h3>
          <div className="space-y-4">
            {teamMembers.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                    <span className="text-indigo-600 font-bold">
                      {member.name?.charAt(0).toUpperCase() || 'T'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{member.name || 'Unknown'}</p>
                    <p className="text-sm text-gray-600">{member.email || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 text-xs font-medium rounded ${getStatusColor(attendanceStatus[member.id])}`}>
                    {attendanceStatus[member.id] || 'Not Marked'}
                  </span>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleMarkAttendance(member.id, 'present')}
                      className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-xs"
                    >
                      Present
                    </button>
                    <button
                      onClick={() => handleMarkAttendance(member.id, 'halfday')}
                      className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-xs"
                    >
                      Half Day
                    </button>
                    <button
                      onClick={() => handleMarkAttendance(member.id, 'absent')}
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                    >
                      Absent
                    </button>
                    <button
                      onClick={() => handleMarkAttendance(member.id, 'office_leave')}
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
                    >
                      Leave
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceManagement;

